import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import BlogCard from "@/components/blog/BlogCard";
import BlogSidebar from "@/components/blog/BlogSidebar";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";

interface Post {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  cover_image_url: string | null;
  cover_image_alt: string | null;
  published_at: string | null;
  author_name: string;
  category_id: string | null;
  categories?: { name: string; slug: string } | null;
}

interface Category {
  id: string;
  name: string;
  slug: string;
}

const Blog = () => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const [postsRes, catsRes] = await Promise.all([
        supabase
          .from("posts")
          .select("id, title, slug, excerpt, cover_image_url, cover_image_alt, published_at, author_name, category_id, categories(name, slug)")
          .eq("status", "published")
          .order("published_at", { ascending: false }),
        supabase.from("categories").select("id, name, slug").order("name"),
      ]);
      setPosts((postsRes.data as unknown as Post[]) || []);
      setCategories(catsRes.data || []);
      setLoading(false);
    };
    fetchData();
  }, []);

  const filtered = posts.filter((p) => {
    const matchesSearch = !search || p.title.toLowerCase().includes(search.toLowerCase()) || (p.excerpt || "").toLowerCase().includes(search.toLowerCase());
    const matchesCategory = !activeCategory || p.categories?.slug === activeCategory;
    return matchesSearch && matchesCategory;
  });

  const recentPosts = posts.slice(0, 5).map((p) => ({ slug: p.slug, title: p.title, published_at: p.published_at }));

  return (
    <section className="section-shell">
      <header className="mb-10 max-w-3xl space-y-3">
        <p className="section-title">Blog</p>
        <h1 className="section-heading">Reflexões, ensinos e notícias</h1>
        <p className="section-subtitle">
          Artigos sobre fé, missões, educação cristã e ação social — conteúdo para edificar e inspirar.
        </p>
      </header>

      <div className="mb-8 max-w-md">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Buscar artigos..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      <div className="grid gap-10 lg:grid-cols-[1fr_280px]">
        <div>
          {loading ? (
            <div className="grid gap-6 sm:grid-cols-2">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="card-ambient animate-pulse">
                  <div className="aspect-[16/9] bg-muted" />
                  <div className="space-y-2 p-5">
                    <div className="h-3 w-20 rounded bg-muted" />
                    <div className="h-5 w-3/4 rounded bg-muted" />
                    <div className="h-3 w-full rounded bg-muted" />
                  </div>
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-20 text-center">
              <p className="text-lg text-muted-foreground">Nenhum artigo encontrado.</p>
              <p className="text-sm text-muted-foreground mt-1">Em breve publicaremos novos conteúdos!</p>
            </div>
          ) : (
            <div className="grid gap-6 sm:grid-cols-2">
              {filtered.map((post) => (
                <BlogCard
                  key={post.id}
                  slug={post.slug}
                  title={post.title}
                  excerpt={post.excerpt}
                  coverImageUrl={post.cover_image_url}
                  coverImageAlt={post.cover_image_alt}
                  publishedAt={post.published_at}
                  categoryName={post.categories?.name}
                  authorName={post.author_name}
                />
              ))}
            </div>
          )}
        </div>

        <BlogSidebar
          categories={categories}
          recentPosts={recentPosts}
          activeCategory={activeCategory}
          onCategoryChange={setActiveCategory}
        />
      </div>
    </section>
  );
};

export default Blog;
